import {
  Divider,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Button,
  Box,
  Badge,
  Grid,
  GridItem,
  useTheme,
} from "@chakra-ui/react";
import IssbContainer from "./IssbContainer";
import React, { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { H1, H2, H4 } from "components/text/headings";
import reportDocService from "api/issb/reportdoc.service";
import { useQuery, useQueryClient } from "react-query";
import { useAuth } from "context";
import PageHeader from "components/text/PageHeader";
import { Clock, Sparkle, User } from "@phosphor-icons/react";
import { CaretCircleRight, CaretCircleUp, MagicWand } from "@phosphor-icons/react";
import { routes } from "config/paths";
import { colors } from "theme/foundations/colors";
import WithAuthorization from "context/Auth/WithAuthorization";
import { formatDate } from "utils";
import A, { AButton } from "components/A";
import CreateButton from "components/CreateButton";
import { Card } from "components/Card";
import LifecycleState from "api/common/LifecycleState";
import { ReportDocStateToggle } from "./ReportDocEditorPanel";
// Using this api for fetching the username
import api from "api/user-administration/user-administration";
import { apiGetThreads, taskService } from "api/ai/ai.service";

export function ReportDocHeader() {
  return (
    <PageHeader
      icon={<Sparkle weight="fill" size={32} color={colors.navIconLime} />}
      title="AI Insights" /* Note: figma is inconsistent on AI Insights vs Insights AI */
      subTitle="AI-Powered sustainability insights and report generation from your vetted disclosures and ESG data"
    />
  );
}

const ReportDocListPage = () => {
  const roundSelector = useSelector((state) => state.issbRound);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const createReportDocAndGotoEditor = () => {
    // make a stub report doc if none exists, and get an id
    const stubReportDoc = { state: LifecycleState.PROCESSING };
    reportDocService.create(stubReportDoc).then((newReportDoc) => {
      console.log("newReportDoc", newReportDoc.id);
      // To prevent stale processing after navigation takes place.
      /** 
        Manually putting data into React Query’s cache for the specific reportDoc query key, 
        so that when another part of your app asks for it later, it doesn’t get an old/stale value (or an empty cache)
      **/
      queryClient.setQueriesData(["reportDoc", newReportDoc.id], newReportDoc);

      queryClient.invalidateQueries(["reportDocList", roundSelector?.selectedRound]);
      navigate(routes.reportDocEditor.replace(":id", newReportDoc.id));
    });
  };

  return (
    <IssbContainer>
      <ReportDocHeader />
      <Box display="flex" flexDirection="column" gap={4} width="100%">
        <H2>Reports</H2>
        <CreateButton
          title="Generate new report"
          text="Our AI will help you create a tailored report for your stakeholders"
          onClick={createReportDocAndGotoEditor}
        />

        <ReportDocList round={roundSelector?.selectedRound} />
      </Box>
    </IssbContainer>
  );
};

function ReportDocList({ round }) {
  const { getAccessTokenSilently } = useAuth();

  const reportDocQueryClient = useQueryClient();

  // TODO filter by round.id in sourcesAllowed
  const {
    data: reportDocsPage,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["reportDocList", round],
    queryFn: () =>
      getAccessTokenSilently().then(() =>
        reportDocService.list({
          sort: "lastModifiedDate,desc",
        }, "/summary?size=50")
      ),
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  });
  // To run on every page load everytime everywhere.
  useEffect(() => {
    reportDocQueryClient.invalidateQueries({ queryKey: ["reportDocList", round] });
  }, [reportDocQueryClient, round]);


  // let reportDocs = reportDocsPage?.content || [];
  // reportDocs = reportDocs.filter((reportDoc) => reportDoc.content || reportDoc.title); // filter out blanks, since we make a stub at the start
  
  const reportDocs = useMemo(() => {
    const list = reportDocsPage?.content ?? [];
    // filter out blanks, since we make a stub at the start
    return list.filter((d) => d.content || d.title);
  }, [reportDocsPage?.content]);


  /*
    Issue being faced here : ist query (reportDocService.list) refreshes every 5s and shows the correct latest state (e.g. DRAFT).
    But the per-doc query (useQuery(["reportDoc", id], ...) on the editor page might still hold an old cached version (e.g. still PROCESSING)
    This is why even if the List card shows DRAFT, when I click it, it shows me the page meant for PROCESSING state.

    Solution here: Use useEffect hook to walk through the freshly processed lists and push each row into per-doc cache.
  */

  // Sync each doc's individual cache with the fresher list row
  useEffect(() => {
    if (!reportDocs?.length) return;
    for (const report of reportDocs) {
      reportDocQueryClient.setQueryData(["reportDoc", report.id], (prev) => {
        if (!prev) return report;
        // only replace if newer
        return prev.lastModifiedDate < report.lastModifiedDate ? report : prev;
      });
    }
  }, [reportDocQueryClient, reportDocs]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <Flex width={"auto"} flexDir="column" gap={4}>
      {reportDocs.map((reportDoc) => (
        <ReportDocCard key={reportDoc.id} reportDoc={reportDoc} />
      ))}
    </Flex>
  );
}

function ReportDocCard({ reportDoc }) {
  // const userName = null; // we have the ID but not the name reportDoc.createdBy;
  // phosphor icon does not support color name like gray.500
  const { colors } = useTheme();

  // username from id (hopefully cached)
  const { data: userName } = useQuery({
	// Note: lastModifiedBy is often the AI, so we use createdBy to get the human user name
    queryKey: ["userName", reportDoc.createdBy],
    queryFn: () => api.getUserById(reportDoc.createdBy).then((user) => user.username),
    enabled: !!reportDoc.createdBy,
  });
  // Task if processing
  const { data: task } = useQuery({
	queryKey: ["task", reportDoc.taskId],
	queryFn: () => {
		return taskService.get(reportDoc.taskId)
	},
	onSuccess: (data) => {
		if (data.status === "failed") {
			console.log("Report doc task=failed", "task", data, "reportDoc", reportDoc);
		}
	},
    enabled: !!(reportDoc.state === LifecycleState.PROCESSING && reportDoc.taskId)
  });
  // thread
  const {data: thread} = useQuery({
    queryKey: ["thread", reportDoc.id],
    queryFn: () => apiGetThreads({ reportDocId: reportDoc.id }).then((threads) => threads[0]),
    enabled: !!(reportDoc.state === LifecycleState.PROCESSING),
	refetchInterval: 10000,
	refetchIntervalInBackground: true,
  });

  return (
    <Card
      opacity={1}
      w="100%"
      color="teal"
      borderWidth="1px"
      borderRadius="lg"
      borderColor="gray.200"
      boxShadow="sm"
      bg="gray.50"
    >
      <A href={routes.reportDocEditor.replace(":id", reportDoc.id)}>
        <Box display="flex" flexDirection="column" gap={2} fontWeight="normal" maxHeight="100%">
          <ReportDocStateToggle reportDoc={reportDoc} task={task} thread={thread} />
          <H4>{reportDoc.title}</H4>
          <Box display="flex" flexDirection="row" gap={2}>
            <User size={16} />
            <Text>Last Modified By: {userName}</Text>
          </Box>
          <Box display="flex" flexDirection="row" gap={2}>
            <Clock size={16} />
            <Text>
              {/* Created: {userName} {formatDate(reportDoc.createdDate)} */}
              Last Modified on: {formatDate(reportDoc.lastModifiedDate)}
            </Text>
          </Box>
        </Box>
      </A>
    </Card>
  );
}

export default WithAuthorization(ReportDocListPage);
